/**
 * Menu de overflow do card de negociação no painel de contato (chat).
 * Dropdown alinhado ao canto, z-index acima do painel; ações delegadas ao pai.
 */
import React, { useState, useCallback } from 'react';
import { AppIcon } from '@/components/icons/AppIcon';
import ConfirmationModal from '@/components/shared/ConfirmationModal';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface DealCardOverflowMenuProps {
    leadName: string;
    canCreateLeads: boolean;
    canDeleteLeads: boolean;
    onGoToKanban: () => void;
    onDuplicate: () => void;
    onDeleteConfirmed: () => void | Promise<void>;
}

export const DealCardOverflowMenu: React.FC<DealCardOverflowMenuProps> = ({
    leadName,
    canCreateLeads,
    canDeleteLeads,
    onGoToKanban,
    onDuplicate,
    onDeleteConfirmed
}) => {
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const handleConfirmDelete = useCallback(async () => {
        await onDeleteConfirmed();
        setDeleteModalOpen(false);
    }, [onDeleteConfirmed]);

    return (
        <>
            <div
                role="toolbar"
                aria-label="Ações da negociação"
                className="inline-flex shrink-0"
            >
                <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            className={cn(
                                'mt-0.5 inline-flex size-11 shrink-0 items-center justify-center rounded-md text-muted transition-colors pointer-fine:size-8',
                                'hover:bg-surface-subtle hover:text-default',
                                'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                                'data-[state=open]:bg-surface-subtle data-[state=open]:text-default'
                            )}
                            title="Mais opções"
                            aria-label="Mais opções da negociação"
                            aria-haspopup="menu"
                        >
                            <AppIcon name="more" className="h-4 w-4" aria-hidden />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        side="bottom"
                        sideOffset={6}
                        className="z-[120] w-56 max-w-[calc(100vw-2rem)]"
                    >
                        <DropdownMenuItem onSelect={() => void onGoToKanban()}>
                            <AppIcon name="arrow-right" className="h-3.5 w-3.5 opacity-80" aria-hidden />
                            Ir para negociações
                        </DropdownMenuItem>

                        {canCreateLeads && (
                            <DropdownMenuItem onSelect={() => void onDuplicate()}>
                                <AppIcon name="duplicate" className="h-3.5 w-3.5 opacity-80" aria-hidden />
                                Duplicar negociação
                            </DropdownMenuItem>
                        )}

                        {canDeleteLeads && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    variant="destructive"
                                    onSelect={() => setDeleteModalOpen(true)}
                                >
                                    <AppIcon name="trash" className="h-3.5 w-3.5 opacity-90" aria-hidden />
                                    Excluir negociação
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <ConfirmationModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={() => void handleConfirmDelete()}
                title="Excluir negociação"
                message={`Tem certeza que deseja excluir "${leadName}"? Esta ação não pode ser desfeita.`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />
        </>
    );
};
